import { AsyncFn, IsoDateString } from '../../common'
import { Address } from '../Address/Address'
import { CompanyGuid } from '../Company/Company'
import { PdfLayoutConfig } from '../Company/PdfLayoutConfig'
import { EmailAddressValue } from '../Email/EmailTypes'
import { JobClass } from '../Job/JobClass'
import { PhoneNumberValue } from '../PhoneNumbers/Phone'
import { MaintenancePlanGuid, MaintenancePlanStatus } from './MaintenancePlanTypes'

export const unauthMaintenancePlanConfirmationDataProcedurePath =
  'unauth:maintenance-plans:get-confirmation-data' as const

export const maintenancePlanConfirmationCustomerFacingDiscountJobClasses: ReadonlySet<JobClass> = new Set([
  JobClass.SERVICE,
  JobClass.MAINTENANCE,
  JobClass.INSTALL,
])

/**
 * Terminal plan statuses for which a confirmation PDF can be downloaded.
 *
 * ACTIVE + LAPSED + CANCELED + EXPIRED all have an `activatedAt` timestamp and
 * render the full confirmation. PENDING/SCHEDULED are excluded — they go down
 * the status-only branch of {@link MaintenancePlanConfirmationPortalData}.
 */
export type MaintenancePlanConfirmationDownloadableStatus =
  | MaintenancePlanStatus.ACTIVE
  | MaintenancePlanStatus.LAPSED
  | MaintenancePlanStatus.CANCELED
  | MaintenancePlanStatus.EXPIRED

/** Resolved discount line on a confirmation — see §2.6 of the PRD. */
export type MaintenancePlanConfirmationDiscount = {
  readonly jobClass: JobClass
  readonly discountRate: number
}

export type MaintenancePlanConfirmationDiscountSource = {
  readonly discountJobClass: string
  readonly discountRate: number
}

export const isMaintenancePlanConfirmationKnownJobClass = (jobClass: string): jobClass is JobClass =>
  Object.values(JobClass).includes(jobClass as JobClass)

export const resolveMaintenancePlanConfirmationDiscounts = (
  recordDiscounts: ReadonlyArray<MaintenancePlanConfirmationDiscountSource>,
  definitionDiscounts: ReadonlyArray<MaintenancePlanConfirmationDiscountSource> | undefined,
): ReadonlyArray<MaintenancePlanConfirmationDiscount> => {
  const source = recordDiscounts.length > 0 ? recordDiscounts : (definitionDiscounts ?? [])
  return source
    .filter(
      (discount): discount is { readonly discountJobClass: JobClass; readonly discountRate: number } =>
        isMaintenancePlanConfirmationKnownJobClass(discount.discountJobClass) &&
        maintenancePlanConfirmationCustomerFacingDiscountJobClasses.has(discount.discountJobClass),
    )
    .map(discount => ({ jobClass: discount.discountJobClass, discountRate: discount.discountRate }))
}

export type MaintenancePlanConfirmationCoveredEquipment = {
  readonly type: string
  readonly nickname?: string
}

export type MaintenancePlanConfirmationBenefit = {
  readonly name: string
  readonly description: string
}

export type MaintenancePlanConfirmationBillingProfile = {
  readonly businessAddress: Address
  readonly businessLegalName: string
  readonly contractorLicenseNumber?: string
  readonly businessLogoURL: string
  readonly businessBillingEmailAddress: EmailAddressValue
  readonly businessBillingContactPhoneNumber: PhoneNumberValue
  readonly businessWebsiteURL?: string
  readonly businessWebsiteDisplayName?: string
}

/**
 * Fully-composed, downloadable confirmation data. Every field is required.
 *
 * Purpose-built; deliberately NOT a reuse of
 * `UnauthedMaintenancePlanDetailsViewModel` (which is visit-keyed).
 *
 * Display strings under `financial` centralize formatting logic in the reader
 * so the PDF template and the portal render the same strings — no double
 * formatting, no divergence risk.
 */
export type MaintenancePlanConfirmationData = {
  readonly companyGuid: CompanyGuid
  readonly maintenancePlanGuid: MaintenancePlanGuid
  /** Always set — reader throws NotFoundException otherwise. */
  readonly activatedAt: IsoDateString
  readonly status: MaintenancePlanConfirmationDownloadableStatus
  readonly statusDetails: {
    readonly canceledAt?: IsoDateString
    readonly lapsedAt?: IsoDateString
    readonly expiredAt?: IsoDateString
  }
  /** `#${first-8-upper(planGuid)}` — matches activation email subject convention. */
  readonly referenceDisplay: string

  readonly company: {
    readonly name: string
    readonly billingProfile: MaintenancePlanConfirmationBillingProfile
    readonly timezone: string
    readonly pdfLayoutConfig: PdfLayoutConfig
  }

  readonly customer: {
    readonly primaryContactName: string
    readonly accountDisplayName: string
    readonly accountReferenceNumber?: string
    readonly serviceAddress: Address
  }

  readonly plan: {
    readonly name: string
    readonly yearlyVisits: number
    readonly coveredEquipment: ReadonlyArray<MaintenancePlanConfirmationCoveredEquipment>
    readonly benefits: ReadonlyArray<MaintenancePlanConfirmationBenefit>
    /** Resolved per §2.6 discount rule: plan override if non-empty, else definition. */
    readonly discounts: ReadonlyArray<MaintenancePlanConfirmationDiscount>
    readonly termsAndConditions: string
  }

  readonly financial: {
    /** e.g. "$420.00" */
    readonly yearlyPriceDisplay: string
    /** e.g. "Monthly — $35.00 · Visa ••••1234" or "No charge" */
    readonly paymentDisplay: string
    /** Human string from queryable projection, e.g. "1st of the month". */
    readonly billingAnchorDisplay: string
  }
}

/**
 * Portal query output. Discriminated union — matches the access matrix from
 * §2.4 of the PRD.
 *
 * PENDING/SCHEDULED plans have no pricing/configuration guarantees, so they go
 * down the `downloadable: false` branch. The type system makes it impossible
 * to render a Download button without the full confirmation.
 */
export type MaintenancePlanConfirmationPortalData =
  | MaintenancePlanConfirmationPortalDataStatusOnly
  | MaintenancePlanConfirmationPortalDataDownloadable

export type MaintenancePlanConfirmationPortalDataStatusOnly = {
  readonly downloadable: false
  readonly maintenancePlanGuid: MaintenancePlanGuid
  /** For branding on the status-only page. */
  readonly companyName: string
  /**
   * Company timezone so the portal can render `scheduledActivationAt` in the
   * customer's expected zone (matches what the CSR configured). Without this,
   * the display could be off by a day for scheduled dates near midnight UTC.
   */
  readonly companyTimezone: string
  readonly referenceDisplay: string
  readonly status: MaintenancePlanStatus.PENDING | MaintenancePlanStatus.SCHEDULED
  readonly statusDetails: {
    readonly scheduledActivationAt?: IsoDateString
  }
}

export type MaintenancePlanConfirmationPortalDataDownloadable = {
  readonly downloadable: true
  /** Portal page uses this to build the unauth download URL. */
  readonly companyGuid: CompanyGuid
  readonly confirmation: MaintenancePlanConfirmationData
}

export type MaintenancePlanConfirmationReader = AsyncFn<
  {
    readonly maintenancePlanGuid: MaintenancePlanGuid
    readonly companyGuid: CompanyGuid
  },
  MaintenancePlanConfirmationData
>

export type MaintenancePlanConfirmationPortalReader = AsyncFn<
  { readonly companyGuid: CompanyGuid; readonly maintenancePlanGuid: MaintenancePlanGuid },
  MaintenancePlanConfirmationPortalData
>

export type MaintenancePlanConfirmationPdfGenerator = AsyncFn<
  {
    readonly maintenancePlanGuid: MaintenancePlanGuid
    readonly companyGuid: CompanyGuid
  },
  Buffer
>

/** Display names for payment intervals as they appear on the confirmation PDF. */
export const maintenancePlanConfirmationPaymentIntervalDisplay = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  YEARLY: 'Annual',
  UPFRONT: 'One-time',
} as const

/**
 * Builds the human-readable reference string from a plan guid, e.g.
 * `"#A1B2C3D4"`. Matches the activation email subject convention.
 */
export const buildMaintenancePlanReferenceDisplay = (maintenancePlanGuid: string): string =>
  `#${maintenancePlanGuid.substring(0, 8).toUpperCase()}`

const normalizeConfirmationName = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9,\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const buildConfirmationNameComparisonValues = (value: string): ReadonlySet<string> => {
  const normalized = normalizeConfirmationName(value)
  const values = new Set<string>()
  if (!normalized) return values

  values.add(normalized.replace(/,/g, '').replace(/\s+/g, ' ').trim())

  if (normalized.includes(',')) {
    const [lastName, firstName] = normalized.split(',').map(part => part.trim())
    if (firstName && lastName) values.add(`${firstName} ${lastName}`.replace(/\s+/g, ' ').trim())
  }

  return values
}

export const shouldRenderMaintenancePlanConfirmationAccountDisplayName = ({
  primaryContactName,
  accountDisplayName,
}: {
  readonly primaryContactName: string
  readonly accountDisplayName: string
}): boolean => {
  if (!accountDisplayName.trim()) return false
  if (!primaryContactName.trim()) return true

  const primaryValues = buildConfirmationNameComparisonValues(primaryContactName)
  const accountValues = buildConfirmationNameComparisonValues(accountDisplayName)
  return ![...accountValues].some(value => primaryValues.has(value))
}

/**
 * Deterministic filename for a confirmation PDF.
 *
 * - Lowercases, strips non-alphanumeric, caps slug at 50 chars.
 * - Falls back to `plan-confirmation-{first-8-of-guid}.pdf` when the plan name
 *   slug is empty (pattern from GenerateAndEmailInvoiceV2Pdf.ts:30-42).
 */
export const buildConfirmationFilename = (planName: string | undefined, maintenancePlanGuid: string): string => {
  const slug = (planName ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 50)

  if (slug.length === 0) {
    return `plan-confirmation-${maintenancePlanGuid.substring(0, 8)}.pdf`
  }
  return `plan-confirmation-${slug}.pdf`
}
